import { getUseAgora } from "./config/sdkConfig";

// VideoSDK configuration
const VIDEOSDK_API_BASE_URL = "https://api.videosdk.live";
const VIDEOSDK_TOKEN = process.env.REACT_APP_VIDEOSDK_TOKEN;
const API_AUTH_URL = process.env.REACT_APP_AUTH_URL;

// Agora configuration
const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID;
const BACKEND_API_URL = process.env.REACT_APP_BASE_URL;

/**
 * Generate a unique Agora channel/meeting ID
 * Format: xxxx-xxxx-xxxx (similar to VideoSDK but prefixed for clarity)
 */
const generateAgoraMeetingId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const segments = [];
  for (let i = 0; i < 3; i++) {
    let segment = '';
    for (let j = 0; j < 4; j++) {
      segment += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    segments.push(segment);
  }
  return segments.join('-');
};

/**
 * Get token - returns VideoSDK token or Agora token based on backend config
 */
export const getToken = async () => {
  if (getUseAgora()) {
    // For Agora, we'll get the token when joining a specific channel
    // Return the app ID as a placeholder - actual token is fetched in createMeeting/validateMeeting
    return AGORA_APP_ID;
  }

  // VideoSDK token logic
  if (VIDEOSDK_TOKEN && API_AUTH_URL) {
    console.error(
      "Error: Provide only ONE PARAMETER - either Token or Auth API"
    );
  } else if (VIDEOSDK_TOKEN) {
    return VIDEOSDK_TOKEN;
  } else if (API_AUTH_URL) {
    const res = await fetch(`${API_AUTH_URL}/get-token`, {
      method: "GET",
    });
    const { token } = await res.json();
    return token;
  } else {
    console.error("Error: ", Error("Please add a token or Auth Server URL"));
  }
};

/**
 * Get Agora RTC token from backend
 */
const getAgoraToken = async (channelName, uid = 0) => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/agora/rtc-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channelName, uid }),
    });
    const data = await response.json();
    if (data.token) {
      return { token: data.token, appId: data.appId, err: null };
    }
    return { token: null, appId: null, err: data.error || "Failed to get Agora token" };
  } catch (error) {
    console.error("Error getting Agora token:", error);
    return { token: null, appId: null, err: error.message };
  }
};

/**
 * Create a meeting - uses Agora or VideoSDK based on backend config
 */
export const createMeeting = async ({ token }) => {
  if (getUseAgora()) {
    // For Agora, generate a unique channel name as the meeting ID
    const meetingId = generateAgoraMeetingId();

    // Verify we can get a token for this channel (validates Agora credentials)
    const { token: agoraToken, err } = await getAgoraToken(meetingId);

    if (agoraToken) {
      console.log("[Agora] Created meeting:", meetingId);
      return { meetingId, err: null };
    } else {
      console.error("[Agora] Failed to create meeting:", err);
      return { meetingId: null, err };
    }
  }

  // VideoSDK create meeting
  const url = `${VIDEOSDK_API_BASE_URL}/v2/rooms`;
  const options = {
    method: "POST",
    headers: { Authorization: token, "Content-Type": "application/json" },
  };

  const response = await fetch(url, options);
  const data = await response.json();

  if (data.roomId) {
    return { meetingId: data.roomId, err: null };
  } else {
    return { meetingId: null, err: data.error };
  }
};

/**
 * Validate a meeting - uses Agora or VideoSDK based on USE_AGORA flag
 */
export const validateMeeting = async ({ roomId, token }) => {
  if (getUseAgora()) {
    // For Agora, validate by checking if we can get a token for this channel
    // Any valid channel name format is acceptable for Agora
    const meetingIdPattern = /^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/;

    if (!meetingIdPattern.test(roomId)) {
      // Also accept VideoSDK-style IDs for backwards compatibility
      const videosdkPattern = /^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/;
      if (!videosdkPattern.test(roomId)) {
        // Accept any alphanumeric string as a valid Agora channel
        if (!/^[a-zA-Z0-9_-]+$/.test(roomId)) {
          return { meetingId: null, err: "Invalid meeting ID format" };
        }
      }
    }

    // Try to get a token - if successful, the meeting is valid
    const { token: agoraToken, err } = await getAgoraToken(roomId);

    if (agoraToken) {
      console.log("[Agora] Validated meeting:", roomId);
      return { meetingId: roomId, err: null };
    } else {
      return { meetingId: null, err: err || "Failed to validate meeting" };
    }
  }

  // VideoSDK validate meeting
  const url = `${VIDEOSDK_API_BASE_URL}/v2/rooms/validate/${roomId}`;

  const options = {
    method: "GET",
    headers: { Authorization: token, "Content-Type": "application/json" },
  };

  const response = await fetch(url, options);

  if (response.status === 400) {
    const data = await response.text();
    return { meetingId: null, err: data };
  }

  const data = await response.json();

  if (data.roomId) {
    return { meetingId: data.roomId, err: null };
  } else {
    return { meetingId: null, err: data.error };
  }
};

/**
 * Get Agora token for a specific meeting/channel
 * This is used by components that need the actual Agora token
 */
export const getAgoraMeetingToken = async (meetingId, uid = 0) => {
  return getAgoraToken(meetingId, uid);
};

/**
 * Check if currently using Agora
 */
export const isUsingAgora = () => getUseAgora();
